import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminDashboardService } from '../services/adminDashboardService';

const IMPERSONATION_COOKIE_KEY = 'impersonationToken';
const IMPERSONATION_USER_KEY = 'impersonatedUser';

// Initialize state from storage synchronously (no API call needed)
const getInitialState = () => {
  const token = sessionStorage.getItem(IMPERSONATION_COOKIE_KEY);
  const storedUser = localStorage.getItem(IMPERSONATION_USER_KEY);
  if (token && storedUser) {
    try {
      return { token, user: JSON.parse(storedUser), active: true };
    } catch {
      return { token: null, user: null, active: false };
    }
  }
  return { token: null, user: null, active: false };
};

export const useImpersonation = () => {
  const initial = getInitialState();
  const [isImpersonating, setIsImpersonating] = useState(initial.active);
  const [impersonatedUser, setImpersonatedUser] = useState(initial.user);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const clearImpersonation = useCallback(() => {
    sessionStorage.removeItem(IMPERSONATION_COOKIE_KEY);
    localStorage.removeItem(IMPERSONATION_USER_KEY);
    setIsImpersonating(false);
    setImpersonatedUser(null);
  }, []);

  const startImpersonation = useCallback(async (userId, userType, verificationPayload = null) => {
    try {
      setIsLoading(true);
      const response = await adminDashboardService.startImpersonation(
        userType,
        userId,
        verificationPayload
      );

      // Store the impersonation token in sessionStorage (never a cookie --
      // it's a bearer credential sent via X-Impersonation-Token, not auto-attached).
      sessionStorage.setItem(IMPERSONATION_COOKIE_KEY, response.impersonation_token);

      // Store user details in localStorage
      localStorage.setItem(IMPERSONATION_USER_KEY, JSON.stringify(response.user_details));

      setImpersonatedUser(response.user_details);
      setIsImpersonating(true);

      // Redirect based on user type
      if (userType === 'Company_Admin') {
        navigate('/org-admin/dashboard');
      } else if (userType === 'HR') {
        navigate('/hr/dashboard');
      }

      return { success: true, user: response.user_details };
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to start impersonation' };
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const exitImpersonation = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = sessionStorage.getItem(IMPERSONATION_COOKIE_KEY);

      if (token) {
        await adminDashboardService.exitImpersonation(token);
      }

      clearImpersonation();

      // Redirect back to impersonation page
      navigate('/geniehireadmin/impersonate');

      return { success: true };
    } catch (error) {
      console.error('Error exiting impersonation:', error);
      // Even if API fails, clear local state
      clearImpersonation();
      navigate('/geniehireadmin/impersonate');
      return { success: false, error: error.response?.data?.detail || 'Failed to exit impersonation' };
    } finally {
      setIsLoading(false);
    }
  }, [navigate, clearImpersonation]);

  return {
    isImpersonating,
    impersonatedUser,
    isLoading,
    startImpersonation,
    exitImpersonation,
    clearImpersonation
  };
};

export default useImpersonation;
